import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Skull, Loader2 } from 'lucide-react';
import { FactList, SubHeading, Note, type Fact } from '../GuidePanel';
import { StepList } from '../StepList';
import { NpcCard } from '../NpcCard';
import { CheckBox } from '../ChecklistBar';
import { ValidationBadge } from '../ValidationBadge';
import { QuestDropPanel } from './QuestDropPanel';
import { ConfigAuditPanel } from '../ConfigAuditPanel';
import { questAuditFor } from '../../../data/guide/sourceAudit';
import { EntityThumb } from '../../world/EntityThumb';
import { ItemThumb } from '../../world/ItemSprite';
import { npcs } from '../../../data/guide/npcs';
import { classCodeOf, formatZen, stageName } from '../../../utils/questView';
import { characterClasses } from '../../../data/guide/classes';
import { itemPath, mapPath, monsterPath } from '../../../utils/worldFormat';
import type { Checklist } from '../../../hooks/useChecklist';
import type { WorldLookup } from '../../../utils/progressionLookup';
import type { CharacterClass } from '../../../types/progression';
import type { QuestView } from '../../../utils/questView';
type Props = {
  questId: number;
  cls?: CharacterClass;
  list: Checklist;
  prefix: string;
  lookup?: WorldLookup;
  status: string;
  steps: string[];
};
const confirmed = 'documentado' as const;

/** Requisitos, pasos y objetivos de UNA misión, desde los archivos exportados y filtrados por clase. */
export function QuestBlock({
  questId,
  cls,
  list,
  prefix,
  lookup,
  status,
  steps
}: Props) {
  if (!lookup) {
    return <p className="flex items-center gap-2 text-sm text-gray-400" data-id="element-1410" data-mp="zyxikknkknd">
        <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" aria-hidden="true" data-id="element-1411" data-mp="3j5by28efnw0v" />
        {status === 'error' ? 'No se pudieron cargar los datos del servidor.' : 'Cargando requisitos de la misión…'}
      </p>;
  }
  const v = lookup.quest(questId, cls);
  if (!v) return <Note tone="warn" data-id="element-1412" data-mp="1lf0zsl8pp2gt">Esta misión no figura en los archivos de misiones cargados. Requisitos pendientes de confirmar.</Note>;
  if (cls && !v.eligible) return <Note data-id="element-1413" data-mp="2ucr1uhpdifhg">{cls.base} no tiene ninguna regla de inicio para “{v.title}” en la configuración.</Note>;
  const npcId = npcs.find(n => n.monsterId === v.npcMonsterId)?.id;
  // El efecto se muestra una vez por grupo de misiones (en la última etapa), no en cada una.
  const auditEntry = questAuditFor(questId);
  const audit = auditEntry && auditEntry[1].questIds[auditEntry[1].questIds.length - 1] === questId ? auditEntry : undefined;
  // Sin clase elegida: si cada objeto es de clases distintas, se presentan como alternativas, no como lista a reunir.
  const classSets = new Set(v.items.map(it => it.classes.map(c => c.classCode).sort().join(',')));
  const alternatives = !cls && v.items.length > 1 && classSets.size === v.items.length;
  return <div className="space-y-4" data-id="element-1414" data-mp="l275fpe5vne">
      <FactList facts={facts(v, cls)} data-id="element-1415" data-mp="m3gnfqrea30" />
      {npcId ? <NpcCard npcId={npcId} lookup={lookup} data-id="element-1416" data-mp="3rzhnl63i6s6s" /> : v.npcName && <p className="text-sm text-gray-300" data-id="element-1417" data-mp="3rxmydfl78dww">NPC: {v.npcName}</p>}

      <SubHeading data-id="element-1418" data-mp="35rt143noahz">Pasos</SubHeading>
      <StepList steps={steps} list={list} prefix={prefix} data-id="element-1419" data-mp="36t2x58xqqy4" />

      {(v.items.length > 0 || v.kills.length > 0) && <SubHeading data-id="element-1420" data-mp="865oznrzftzn">Objetivos</SubHeading>}
      {alternatives && <Note data-id="element-1421" data-mp="d239856d9khp">
          <strong className="text-white" data-id="element-1422" data-mp="d1l2ufpzppw2">Necesitas solo uno de estos objetos, según tu clase.</strong> Elige tu clase arriba para ver únicamente tus requisitos y objetivos.
        </Note>}
      {v.items.length > 0 && <ul className="grid gap-2" data-id="element-1423" data-mp="m1zz2a4hspec">
          {v.items.map(it => {
        const key = `${prefix}:${it.objective.id}`;
        const have = list.isChecked(key);
        const classNames = !cls ? Array.from(new Set(it.classes.map(c => classLabel(c.classCode)))).join(', ') : undefined;
        return <li key={it.objective.id} className={`rounded-2xl border p-3.5 transition-colors duration-150 ${have ? 'border-[#D4AF37]/40 bg-[#D4AF37]/[0.05]' : 'border-white/10 bg-[#0A1628]'}`} data-id="element-1424" data-mp="14uloocot3jqh">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-1425" data-mp="w6g6163a5fzo">
                  <div className="flex min-w-0 gap-3" data-id="element-1426" data-mp="s5cswrcm9y25">
                  <ItemThumb id={it.variantId ?? it.itemId} name={it.name} data-id="element-1427" data-mp="s4ukyxgldvee" />
                  <div className="min-w-0" data-id="element-1428" data-mp="15v6en0sts27n">
                    <p className="flex flex-wrap items-baseline gap-x-2" data-id="element-1429" data-mp="15yc3qg1gf43d">
                      <span className="font-semibold text-white" data-id="element-1430" data-mp="10y6j3jtlqhus">{it.name}</span>
                      <span className="text-sm text-gray-300" data-id="element-1431" data-mp="d8lpm6zto279">× {it.quantity ?? '—'}</span>
                    </p>
                    {alternatives && classNames && <p className="mt-0.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-1432" data-mp="n603pzy9wewe">Solo para {classNames}</p>}
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1433" data-mp="i6x0ml1usir2">
                      <ValidationBadge status={confirmed} label="Cantidad: confirmada por configuración" data-id="element-1434" data-mp="i5lx8mcbld0h" />
                      {!it.nameFromVariant && it.objective.itemLevel ? <ValidationBadge status="pendiente" label={`Variante nivel ${it.objective.itemLevel} sin nombre en el catálogo`} data-id="element-1435" data-mp="1tmvagr4uv8tc" /> : null}
                    </div>
                    {it.nameSource && <p className="mt-1 text-[11px] text-gray-500" data-id="element-1436" data-mp="3gjbd4d7jp3n3">Nombre: {it.nameSource}</p>}
                    {!alternatives && classNames && <p className="mt-1 text-xs text-gray-500" data-id="element-1437" data-mp="32kbyzzhns756">Clases: {classNames}</p>}
                    {it.drop && <QuestDropPanel drop={it.drop} data-id="element-1438" data-mp="37iwop4zab8fd" />}
                  </div>
                  </div>
                  <div className="shrink-0" data-id="element-1439" data-mp="3v8zc37s97q7n">
                    <CheckBox checked={have} onChange={() => list.toggle(key)} label="Ya lo tengo" data-id="element-1440" data-mp="33vtihr3nsay" />
                  </div>
                </div>
                <Link to={`${itemPath(it.variantId ?? it.itemId)}#donde-conseguirlo`} className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-3.5 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-1441" data-mp="3lgtpyeooo30d">
                  <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1442" data-mp="mtpi6myipray" />
                  Dónde conseguirlo
                </Link>
              </li>;
      })}
        </ul>}
      {v.kills.length > 0 && <ul className="grid gap-2 sm:grid-cols-3" data-id="element-1443" data-mp="mow04mk9i1vv">
          {v.kills.map(k => {
        const key = `${prefix}:${k.objective.id}`;
        return <li key={k.objective.id} className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-1444" data-mp="hsynaq87jdnu">
                <p className="flex items-center gap-2" data-id="element-1445" data-mp="8yah8xut5rxz">
                  <EntityThumb entity={k.monsterId !== undefined ? {
              type: 'monster',
              id: k.monsterId
            } : undefined} name={k.name} fallback={<Skull className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1447" data-mp="3v6jvbxz9u98k" />} data-id="element-1446" data-mp="8wpn39ceob0n" />
                  {k.monsterId !== undefined ? <Link to={monsterPath(k.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1448" data-mp="3m4snamh9dqbj">{k.name}</Link> : <span className="font-semibold text-white" data-id="element-1449" data-mp="25o3di5939wfa">{k.name}</span>}
                </p>
                <p className="mt-1 font-cinzel text-2xl font-black text-white" data-id="element-1450" data-mp="2amy967s5nfgt">× {k.quantity ?? '—'}</p>
                <p className="text-xs text-gray-400" data-id="element-1451" data-mp="2fmswk1cq1mx4">bajas requeridas</p>
                {k.maps.length > 0 ? <p className="mt-1.5 flex flex-wrap gap-x-2 text-xs text-gray-300" data-id="element-1452" data-mp="1hztwdgmtlwuh">
                    {k.maps.map(m => <Link key={m.id} to={mapPath(m.id)} className="underline decoration-white/20 underline-offset-4 hover:text-white" data-id="element-1453" data-mp="1mynddj1bg5ze">{m.name}</Link>)}
                  </p> : <p className="mt-1.5 text-xs text-gray-500" data-id="element-1454" data-mp="1woqb1wjdu842">Apariciones no documentadas</p>}
                <div className="mt-2" data-id="element-1455" data-mp="z7roato8vovu">
                  <CheckBox checked={list.isChecked(key)} onChange={() => list.toggle(key)} label="Hecho" data-id="element-1456" data-mp="147e15205v344" />
                </div>
              </li>;
      })}
        </ul>}

      {audit && <>
          <SubHeading data-id="element-1457" data-mp="3oqw7jn8g4h74">{audit[0] === 'combo' ? 'Combo al completar' : 'Recompensa al completar'}</SubHeading>
          <ConfigAuditPanel fact={audit[1]} title="Requisitos documentados · efecto pendiente" missingTitle="Efecto pendiente de la fuente del servidor" data-id="element-1458" data-mp="6l01ji67c9wh" />
        </>}
    </div>;
}

/** Código de clase de los archivos → nombre de la clase inicial ("DK" → "Dark Knight"). */
const classLabel = (code: string) => characterClasses.find(c => classCodeOf(c.id) === code)?.base ?? code;
function facts(v: QuestView, cls?: CharacterClass): Fact[] {
  const out: Fact[] = [];
  if (v.levelMin !== undefined) out.push({
    label: 'Nivel mínimo',
    value: `${v.levelMin}${v.levelMax !== undefined ? ` – ${v.levelMax}` : ''}`,
    status: confirmed
  });
  if (v.zen !== undefined) out.push({
    label: 'Zen al aceptar',
    value: `${formatZen(v.zen)}${v.zenStates.length > 1 ? ' · mismo importe si se retoma tras cancelarla' : ''}`,
    status: confirmed
  });else if (!v.zenConsistent) out.push({
    label: 'Zen al aceptar',
    value: 'Importes distintos según el estado: ver panel de verificación',
    status: 'pendiente'
  });
  if (cls) {
    const s = v.starts[0];
    const stage = stageName(cls, s?.classes[0]?.changeUpPlusOne);
    if (stage) out.push({
      label: 'Clase requerida',
      value: stage,
      status: confirmed
    });
    out.push({
      label: 'Misión previa',
      value: s?.prereqTitle ? `${s.prereqTitle} (terminada)` : 'Ninguna',
      status: confirmed
    });
  } else {
    const groups = new Map<string, string[]>();
    v.starts.forEach(s => {
      const k = s.prereqTitle ? `${s.prereqTitle} (terminada)` : 'Ninguna';
      groups.set(k, [...(groups.get(k) ?? []), ...s.classes.map(c => c.classCode)]);
    });
    out.push({
      label: 'Misión previa',
      value: Array.from(groups.entries()).map(([k, codes]) => `${Array.from(new Set(codes)).join('/')}: ${k}`).join(' · ') || '—',
      status: confirmed
    });
  }
  return out;
}